@if (uiState === UIStateEnum.ShowLoading) {
  <mat-card class="mt-4">
    <mat-card-content>
      <app-loading-card-content></app-loading-card-content>
    </mat-card-content>
  </mat-card>
} @else {

<form [formGroup]="payoutRuleForm" (ngSubmit)="onSubmit()">

  <mat-card class="mt-4">
    <mat-card-header>
      <mat-card-title>When this version takes effect</mat-card-title>
    </mat-card-header>

    <mat-card-content>
      <p class="rule-explanation">
        A rule is dated. A report splits its whole period by the version in force on the period's last day, so a
        version dated after that day never changes it; adding, editing or voiding one dated on or before that day does.
        Everything below belongs to the date shown here.
      </p>

      <!-- Disabled while a version in force is loaded: moving it would restate periods already reported. A version
           that has not taken effect can be moved here; "Change from today" is the way to a new version. -->
      <mat-form-field class="effective-from-field">
        <mat-label>In effect from</mat-label>
        <input matInput
               type="date"
               formControlName="effectiveFrom"
               data-testid="rule-effective-from-input">
      </mat-form-field>

      <!-- Says which version the button at the bottom will write, in the words of the two things it can do. An
           admin should not have to work that out from a date field. -->
      @if (movingTheVersion) {
        <p class="rule-aside" data-testid="rule-save-target-note">
          Saving moves this version from {{ formatEffectiveFrom(versionBeingEdited?.EffectiveFrom) }} to
          {{ formatEffectiveFrom(dateBeingSaved) }}.
        </p>
      } @else if (!addingAVersion) {
        <p class="rule-aside" data-testid="rule-save-target-note">
          Saving updates the version in effect from {{ formatEffectiveFrom(dateBeingSaved) }}. To change the rule
          from a different date instead, use <strong>Change from today</strong> below.
        </p>
      } @else if (versions.length) {
        <p class="rule-aside" data-testid="rule-save-target-note">
          Saving adds a version in effect from {{ formatEffectiveFrom(dateBeingSaved) }} and leaves every
          existing version as it is.
        </p>
      }

      @if (editingAFutureVersion) {
        <p class="rule-aside" data-testid="rule-future-version-note">
          This date is in the future, so saving schedules the change rather than applying it now. Until then,
          reports keep using the version currently in force.
        </p>
      } @else if (editingAPastVersion) {
        <p class="rule-aside" data-testid="rule-past-version-note">
          This date is in the past. Saving corrects that version, which changes what reports say about periods from
          that date onwards — only do this if the version itself was recorded wrongly.
        </p>
      }

      @if (versions.length) {
        <h4 class="versions-heading">Change history</h4>

        <app-table-export-buttons
          testIdPrefix="rule-versions"
          (exportCsv)="onExportVersionsCsv()"
          (exportPdf)="onExportVersionsPdf()">
        </app-table-export-buttons>

        <table class="versions-table" data-testid="rule-versions-table">
          <thead>
            <tr>
              <th>In effect from</th>
              <th>Rule</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (version of versions; track version.Id) {
              <tr data-testid="rule-version-row"
                  [class.editing]="isVersionBeingEdited(version)"
                  [class.voided]="isVoided(version)">
                <td class="version-date">{{ formatEffectiveFrom(version.EffectiveFrom) }}</td>
                <td>
                  {{ versionSummary(version) }}
                  @if (isVoided(version)) {
                    <div class="void-note" data-testid="rule-version-void-note">
                      Voided {{ formatVoidedOn(version.VoidedOn) }}: {{ version.VoidReason }}
                    </div>
                  }
                </td>
                <td class="version-actions">
                  @if (isVoided(version)) {
                    <span class="voided-label">Voided</span>
                  } @else {
                    @if (!isVersionBeingEdited(version)) {
                      <button mat-stroked-button
                              type="button"
                              (click)="loadVersion(version)"
                              data-testid="load-rule-version-button">
                        Open
                      </button>
                    } @else {
                      <span class="editing-label">Editing</span>
                    }
                    <ng-container *hasPermission="permissionTypes.CausableAdminOnly">
                      <button mat-stroked-button
                              type="button"
                              color="warn"
                              (click)="voidVersion(version)"
                              data-testid="void-rule-version-button">
                        Void
                      </button>
                    </ng-container>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>

        <button mat-stroked-button
                type="button"
                (click)="startNewVersion()"
                data-testid="new-rule-version-button">
          Change from today
        </button>
      }
    </mat-card-content>
  </mat-card>

  <mat-card class="mt-4">
    <mat-card-header>
      <mat-card-title>
        Host admin fee
        <button mat-icon-button
                type="button"
                aria-label="About payout rules"
                (click)="openHelp()"
                data-testid="payout-rules-help-button">
          <mat-icon>help_outline</mat-icon>
        </button>
      </mat-card-title>
    </mat-card-header>

    <mat-card-content>
        <p class="rule-explanation">
          Some organizations take a share of the administration fee already collected on every payment.
          @if (gameChargesAnAdministrationFee) {
            This game's administration fee is
            <strong data-testid="admin-fee-rate">{{ formatPercent(feePercentOfEveryPayment) }}% of every payment</strong>,
            and the shares below divide that one fee between its recipients — which is why they total 100%
            of it rather than adding up to a rate of their own.
          } @else {
            This game currently charges
            <strong data-testid="admin-fee-rate">no administration fee (0% of every payment)</strong>,
            so the shares below divide only what was collected while a fee was still being charged — for a
            game that never charged one, every recipient's amount is $0.00.
          }
        </p>

        <p class="rule-explanation rule-aside">
          Nothing here changes what the charities receive. The administration fee is already deducted from
          every payment before their share is worked out, so the report's charity figures are net of it
          whether or not this fee is shown. This only names who receives it.
        </p>

        <mat-checkbox formControlName="hostAdminFeeEnabled"
                      data-testid="host-admin-fee-toggle">
          This game shows a host admin fee
        </mat-checkbox>

        @if (hostAdminFeeEnabled) {
          <div class="recipients" formArrayName="adminFeeRecipients">
            @for (recipient of recipients.controls; track recipient; let i = $index) {
              <div class="recipient-row" [formGroupName]="i" [attr.data-testid]="'admin-fee-recipient-row-' + i">

                <mat-radio-group formControlName="payeeType"
                                 (change)="onRecipientPayeeTypeChange(recipient)"
                                 [attr.data-testid]="'recipient-payee-type-' + i">
                  <mat-radio-button value="charity" [attr.data-testid]="'recipient-payee-type-charity-' + i">
                    Charity in this game
                  </mat-radio-button>
                  <mat-radio-button value="external" [attr.data-testid]="'recipient-payee-type-external-' + i">
                    Outside organization
                  </mat-radio-button>
                </mat-radio-group>

                @if (recipient.controls.payeeType.value === 'charity') {
                  <mat-form-field class="payee-field">
                    <mat-label>Charity</mat-label>
                    <mat-select formControlName="charityId" [attr.data-testid]="'recipient-charity-' + i">
                      @for (option of charityOptions; track option.id) {
                        <mat-option [value]="option.id">{{ option.name }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                } @else {
                  <mat-form-field class="payee-field">
                    <mat-label>Organization name</mat-label>
                    <input matInput
                           formControlName="externalName"
                           maxlength="200"
                           [attr.data-testid]="'recipient-external-name-' + i">
                  </mat-form-field>
                }

                <mat-form-field class="share-field">
                  <mat-label>Share % of the fee</mat-label>
                  <input matInput
                         type="number"
                         min="0"
                         max="100"
                         step="0.01"
                         formControlName="sharePercent"
                         [attr.data-testid]="'recipient-share-' + i">
                </mat-form-field>

                <!-- The share restated in the unit organizations quote their fee in, so the number typed can
                     be checked against what was agreed without doing the arithmetic in your head. -->
                @if (recipientPercentOfEveryPayment(recipient) !== null) {
                  <span class="share-effect" [attr.data-testid]="'recipient-share-effect-' + i">
                    = {{ formatPercent(recipientPercentOfEveryPayment(recipient)!) }}% of every payment
                  </span>
                }

                <button mat-icon-button
                        type="button"
                        aria-label="Remove recipient"
                        (click)="removeRecipient(i)"
                        [attr.data-testid]="'remove-recipient-' + i">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            }
          </div>

          <div class="recipients-footer">
            <button mat-stroked-button
                    type="button"
                    (click)="addRecipient()"
                    data-testid="add-recipient-button">
              Add recipient
            </button>

            <span class="shares-total"
                  [class.shares-total-invalid]="!sharesTotalOneHundred"
                  data-testid="shares-total">
              Shares total: {{ sharesTotal.toFixed(2) }}% of the fee
              @if (gameChargesAnAdministrationFee) {
                <span class="shares-total-effect">({{ formatPercent(feePercentOfEveryPayment) }}% of every payment when they total 100%)</span>
              }
            </span>
          </div>
        }
    </mat-card-content>
  </mat-card>

  <mat-card class="mt-4">
    <mat-card-header>
      <mat-card-title>Amount to pay the organization</mat-card-title>
    </mat-card-header>

    <mat-card-content>
      <p class="rule-explanation">
        The payment Causable sends the organization running this game.
      </p>

      <mat-checkbox formControlName="hostPayableEnabled"
                    data-testid="host-payable-toggle">
        Causable sends this organization a payment
      </mat-checkbox>

      @if (hostPayableEnabled) {
        <div class="payable-fields">
          <mat-radio-group formControlName="hostPayablePayeeType"
                           (change)="onPayablePayeeTypeChange()"
                           data-testid="payable-payee-type">
            <mat-radio-button value="charity" data-testid="payable-payee-type-charity">
              Charity in this game
            </mat-radio-button>
            <mat-radio-button value="external" data-testid="payable-payee-type-external">
              Outside organization
            </mat-radio-button>
          </mat-radio-group>

          @if (hostPayablePayeeType === 'charity') {
            <mat-form-field class="payee-field">
              <mat-label>Host charity</mat-label>
              <mat-select formControlName="hostPayableCharityId" data-testid="payable-charity">
                @for (option of charityOptions; track option.id) {
                  <mat-option [value]="option.id">{{ option.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          } @else {
            <mat-form-field class="payee-field">
              <mat-label>Organization name</mat-label>
              <input matInput
                     formControlName="hostPayableExternalName"
                     maxlength="200"
                     data-testid="payable-external-name">
            </mat-form-field>
          }

          <div class="payable-components" formArrayName="payableComponents">
            <span class="components-label">What the payment is made of</span>
            <p class="components-hint">
              Tick the figures that make up the payment, and whether each is added or taken off.
            </p>

            @for (component of payableComponents.controls; track component; let i = $index) {
              <div class="component-row" [formGroupName]="i"
                   [attr.data-testid]="'payable-component-' + component.controls.componentType.value">
                <mat-checkbox formControlName="included"
                              [attr.data-testid]="'payable-component-included-' + component.controls.componentType.value">
                  {{ componentLabel(component.controls.componentType.value) }}
                </mat-checkbox>

                @if (component.controls.included.value) {
                  <mat-radio-group formControlName="isAddition"
                                   [attr.data-testid]="'payable-component-sign-' + component.controls.componentType.value">
                    <mat-radio-button [value]="true"
                                      [attr.data-testid]="'payable-component-add-' + component.controls.componentType.value">Add</mat-radio-button>
                    <mat-radio-button [value]="false"
                                      [attr.data-testid]="'payable-component-subtract-' + component.controls.componentType.value">Subtract</mat-radio-button>
                  </mat-radio-group>
                }
              </div>
            }
          </div>
        </div>
      }
    </mat-card-content>
  </mat-card>

  <div class="save-row">
    @if (blockingMessage) {
      <span class="blocking-message" data-testid="payout-rules-blocking-message">{{ blockingMessage }}</span>
    }

    <ng-container *hasPermission="permissionTypes.ModifyGamePayoutRules">
      <button mat-flat-button
              color="primary"
              type="submit"
              [disabled]="!canSave"
              [attr.data-confirms-date]="addingAVersion || movingTheVersion"
              data-testid="save-payout-rules-button">
        Save payout rules
      </button>
    </ng-container>
  </div>

</form>

}
